/**
 * เครื่องส่งคิวออฟไลน์
 *
 * หนึ่งรายการเดินสามขั้น และ "ทำต่อจากจุดเดิม" ได้เสมอ
 *   1. สร้าง draft   -> เก็บ captureId และ upload token
 *   2. อัปโหลดรูป    -> ถ้า token หมดอายุให้ขอใหม่แล้วลองอีกครั้ง
 *   3. finalize      -> ถือว่าสำเร็จ
 *
 * idempotency key คือ id ของรายการในคิว จึงยิงซ้ำได้โดยไม่เกิดข้อมูลซ้ำ
 *
 * ไม่พึ่ง Background Sync API อย่างเดียว เพราะการรองรับบน iOS ต่างจาก Android
 * ตัวกระตุ้นการส่งจึงมีสี่ทาง: เปิดแอป, กลับมาออนไลน์, แอปกลับมาอยู่หน้าจอ, ผู้ใช้กดเอง
 */
import { api, ApiError } from './api';
import {
  allQueued,
  claimableQueued,
  countByStatus,
  deleteQueued,
  getQueued,
  putQueued,
  purgeSent,
  type QueuedCapture,
} from './db';

const MAX_ATTEMPTS = 8;

/** error ที่ลองใหม่ไปก็ไม่มีทางสำเร็จ ต้องให้คนมาแก้ */
const TERMINAL_CODES = new Set([
  'REGISTRATION_INVALID',
  'DUPLICATE_REGISTRATION',
  'GPS_ACCURACY_NOT_MET',
  'GPS_POSITION_STALE',
  'IMAGE_TOO_LARGE',
  'IMAGE_TYPE_NOT_ALLOWED',
  'FORBIDDEN',
  'AREA_FORBIDDEN',
  'USER_INACTIVE',
  'VALIDATION_FAILED',
  'IDEMPOTENCY_KEY_REUSED',
]);

function backoffMs(attempts: number): number {
  const base = Math.min(5000 * 2 ** attempts, 5 * 60_000);
  return base / 2 + Math.random() * (base / 2);
}

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeQueue(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

let running = false;

async function markFailure(item: QueuedCapture, error: unknown): Promise<void> {
  const apiError = error instanceof ApiError ? error : null;
  const code = apiError?.code ?? 'NETWORK_ERROR';
  const terminal = TERMINAL_CODES.has(code) || item.attempts + 1 >= MAX_ATTEMPTS;

  await putQueued({
    ...item,
    status: 'failed',
    attempts: item.attempts + 1,
    // รายการที่แก้เองไม่ได้ ตั้งเวลาไว้ไกลมากเพื่อไม่ให้วนลองซ้ำ ๆ
    nextAttemptAt: terminal ? Number.MAX_SAFE_INTEGER : Date.now() + backoffMs(item.attempts),
    lastErrorCode: code,
    lastErrorMessage:
      apiError?.message ?? 'ส่งไม่สำเร็จเพราะเชื่อมต่อไม่ได้ ระบบจะลองใหม่ให้อัตโนมัติ',
  });
}

/** เดินหนึ่งรายการให้ไกลที่สุดเท่าที่ทำได้ในรอบนี้ */
async function processItem(item: QueuedCapture): Promise<void> {
  await putQueued({ ...item, status: 'sending' });
  notify();

  let current: QueuedCapture = { ...item, status: 'sending' };

  try {
    // ---- ขั้นที่ 1: draft ----
    if (!current.captureId) {
      const response = await api.createDraft(
        {
          customerRegistration: current.customerRegistration,
          gps: current.gps,
          image: {
            contentType: current.imageContentType,
            sizeBytes: current.imageSizeBytes,
            sha256: current.imageSha256,
          },
          idempotencyKey: current.id,
          ...(current.note ? { note: current.note } : {}),
          ...(current.gpsOverrideReason ? { gpsOverrideReason: current.gpsOverrideReason } : {}),
          ...(current.allowDuplicate ? { allowDuplicate: true } : {}),
          ...(current.duplicateReason ? { duplicateReason: current.duplicateReason } : {}),
        },
        current.id,
      );

      current = {
        ...current,
        captureId: response.capture.id,
        uploadToken: response.upload.token,
        uploadTokenExpiresAt: response.upload.expiresAt,
        awaitingGpsApproval: response.awaitingGpsApproval,
      };
      await putQueued(current);
      notify();
    }

    const captureId = current.captureId;
    if (!captureId) throw new Error('ไม่ได้รับรหัสรายการจากเซิร์ฟเวอร์');

    // ---- ขั้นที่ 2: อัปโหลดรูป ----
    if (!current.uploaded) {
      const expired =
        !current.uploadToken ||
        !current.uploadTokenExpiresAt ||
        new Date(current.uploadTokenExpiresAt).getTime() <= Date.now() + 5000;

      if (expired) {
        const reissued = await api.reissueUpload(captureId, {
          contentType: current.imageContentType,
          sizeBytes: current.imageSizeBytes,
        });
        current = {
          ...current,
          uploadToken: reissued.upload.token,
          uploadTokenExpiresAt: reissued.upload.expiresAt,
        };
        await putQueued(current);
      }

      try {
        await api.uploadImage(
          captureId,
          current.imageBlob,
          current.uploadToken!,
          current.imageSha256,
        );
      } catch (error) {
        // token หมดอายุระหว่างทาง: ขอใหม่แล้วลองอีกครั้งทันทีในรอบเดียวกัน
        if (error instanceof ApiError && error.code === 'UPLOAD_AUTHORIZATION_EXPIRED') {
          const reissued = await api.reissueUpload(captureId, {
            contentType: current.imageContentType,
            sizeBytes: current.imageSizeBytes,
          });
          current = {
            ...current,
            uploadToken: reissued.upload.token,
            uploadTokenExpiresAt: reissued.upload.expiresAt,
          };
          await api.uploadImage(
            captureId,
            current.imageBlob,
            current.uploadToken!,
            current.imageSha256,
          );
        } else {
          throw error;
        }
      }

      current = { ...current, uploaded: true };
      await putQueued(current);
      notify();
    }

    // ---- ขั้นที่ 3: finalize ----
    const finalized = await api.finalize(
      captureId,
      {
        sha256: current.imageSha256,
        sizeBytes: current.imageSizeBytes,
        contentType: current.imageContentType,
      },
      `${current.id}-fin`,
    );

    await putQueued({
      ...current,
      status: 'sent',
      awaitingGpsApproval: finalized.awaitingGpsApproval,
      lastErrorCode: null,
      lastErrorMessage: null,
    });
    notify();
  } catch (error) {
    await markFailure(current, error);
    notify();
  }
}

/**
 * ส่งทุกรายการที่ถึงเวลาแล้ว
 * ทำทีละรายการโดยตั้งใจ ไม่ส่งขนานกัน เพราะสัญญาณในสนามมักแคบ
 * การยิงพร้อมกันหลายรายการทำให้ทุกรายการช้าลงและ timeout พร้อมกัน
 */
export async function syncQueue(): Promise<{ processed: number }> {
  if (running) return { processed: 0 };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { processed: 0 };

  running = true;
  let processed = 0;
  try {
    const items = await claimableQueued();
    for (const item of items) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
      await processItem(item);
      processed += 1;
    }
    await purgeSent();
  } finally {
    running = false;
    notify();
  }
  return { processed };
}

/** ผู้ใช้กด "ส่งใหม่" — ล้างเวลาหน่วงแล้วส่งทันที */
export async function retryNow(id?: string): Promise<void> {
  const items = await allQueued();
  for (const item of items) {
    if (id && item.id !== id) continue;
    if (item.status === 'sent' || item.status === 'sending') continue;
    await putQueued({ ...item, status: 'pending', nextAttemptAt: 0 });
  }
  notify();
  await syncQueue();
}

/**
 * ทิ้งรายการในคิวที่ยังส่งไม่สำเร็จ
 *
 * จำเป็นเพราะรายการที่ส่งไม่ผ่านซ้ำ ๆ (เช่น เลขทะเบียนผิดจนเซิร์ฟเวอร์ปฏิเสธทุกครั้ง)
 * จะค้างอยู่ในเครื่องตลอดไป กินพื้นที่และทำให้ตัวเลขบนแถบบนไม่มีวันเป็นศูนย์
 *
 * ทิ้งได้เฉพาะรายการที่ยังไม่ถูกส่งสำเร็จ ถ้าเป็น 'sending' อยู่จะไม่ทิ้ง
 * เพราะอาจถึงเซิร์ฟเวอร์แล้วแต่ยังไม่ได้คำตอบกลับมา ทิ้งตอนนั้นข้อมูลจะหายทั้งสองฝั่ง
 *
 * ผู้เรียกต้องถามยืนยันก่อนเสมอ — ข้อมูลนี้ยังไม่มีที่ไหนอีกเลยนอกจากในเครื่อง
 */
export async function discardQueued(id: string): Promise<boolean> {
  const item = await getQueued(id);
  if (!item) return false;
  if (item.status === 'sending') return false;

  await deleteQueued(id);
  notify();
  return true;
}

export { allQueued, countByStatus };

/** ต่อสายตัวกระตุ้นการส่งทั้งหมด เรียกครั้งเดียวตอนแอปเริ่มทำงาน */
export function installQueueTriggers(): () => void {
  const onOnline = () => void syncQueue();
  const onVisible = () => {
    if (document.visibilityState === 'visible') void syncQueue();
  };

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);

  // ตาข่ายกันพลาด เผื่อ event ข้างบนไม่ยิงบนบางเบราว์เซอร์
  const timer = setInterval(() => void syncQueue(), 60_000);

  void syncQueue();

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}
